import { useEffect, useCallback, useState } from "react";
import { useStore } from "../../../store/store";
import { useTranslation } from "react-i18next";
import { generateSignedEndpoint } from "../../../api/generateSignedEndpoint";
import axios from "axios";
import Loader from "../../../components/Loader/Loader";
import PollCard from "./PollCard";
import { IPoll } from "../../hooks/useSocket/types";

interface IProps {
	liveId: number | undefined;
}

const PublicPolls = (props: IProps) => {
	const { t } = useTranslation();
	const publicPolls = useStore((state) => state.publicPolls);
	const setPublicPolls = useStore((state) => state.setPublicPolls);
	const [loading, setLoading] = useState(true);

	const fetchPublishedPolls = useCallback(async () => {
		if (!props.liveId) return;
		try {
			setLoading(true);
			const endpoint = `/live/polls/${props.liveId}`;
			const signed = await generateSignedEndpoint(endpoint);
			const response = await axios.get(signed);
			const allPolls: IPoll[] = response.data.polls || [];
			const published = allPolls.filter((p) => p.isPublished);
			setPublicPolls(published);
		} catch {
			console.error("Failed to fetch public polls");
		} finally {
			setLoading(false);
		}
	}, [props.liveId, setPublicPolls]);

	useEffect(() => {
		fetchPublishedPolls();
	}, [fetchPublishedPolls]);

	if (loading) {
		return (
			<div className="flex h-full items-center justify-center">
				<Loader />
			</div>
		);
	}

	if (publicPolls.length === 0) {
		return (
			<div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="48"
					height="48"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeLinecap="round"
					strokeLinejoin="round"
					className="text-primary/30"
				>
					<path d="M18 20V10" />
					<path d="M12 20V4" />
					<path d="M6 20v-6" />
				</svg>
				<p className="text-sm text-primary/50">
					{t("live_page.no_polls")}
				</p>
			</div>
		);
	}

	return (
		<div className="flex h-full flex-col overflow-hidden">
			<div className="flex-1 overflow-y-auto p-3">
				<div className="flex flex-col gap-3">
					{publicPolls.map((poll) => (
						<PollCard key={poll.id} poll={poll} liveId={props.liveId} />
					))}
				</div>
			</div>
		</div>
	);
};

export default PublicPolls;
